import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, FontIcon } from '@fluentui/react';
import { useNavigate, useLocation } from 'react-router';
import { useAppContext } from './AppContext';
import SConsciousApiService from '../services/SConsciousApiService';
import type { IConversation } from './Chat';

interface IMessage {
    messageId: string;
    conversationId: string;
    fromUserId: string;
    fromName: string;
    message: string;
    type: 'chat' | 'feedback';
    createdAt: string;
    attachmentUrl?: string;
    attachmentName?: string;
}

interface IAttachmentPreview {
    dataUrl: string;
    displayUrl: string;
    fileName: string;
    mimeType: string;
    isImage: boolean;
}

const EMOJIS = [
    '😀','😂','🥰','😍','🤩','😎','🥳','😅','😭','😤',
    '😱','🤔','🙏','👍','👎','❤️','🔥','✅','🎉','💯',
    '🙌','💪','🤝','👋','✌️','🫶','💬','📎','📸','🖼️',
    '🛡','⭐','🚀','💡','📢','🎯','🔔','💰','🛒','📱',
];

function formatTime(iso: string): string {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}

function isImageUrl(url?: string): boolean {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url) || url.startsWith('data:image/');
}

function isPdfUrl(url?: string): boolean {
    if (!url) return false;
    return /\.pdf(\?|$)/i.test(url);
}

/** Renders a blob-storage image via the /api/getImage streaming proxy.
 *  Uses IntersectionObserver so images load newest-first (visible viewport first). */
function ChatImage({ url, userId, className, style, onClick }: {
    url: string;
    userId: string;
    className?: string;
    style?: React.CSSProperties;
    onClick?: (src: string) => void;
}) {
    const src = React.useMemo(() => {
        let blobName: string;
        try {
            const u = new URL(url.split('?')[0]);
            const segs = u.pathname.replace(/^\//, '').split('/');
            blobName = segs.slice(1).join('/');
        } catch {
            blobName = url.split('/').pop() || '';
        }
        return `/api/getImage?userId=${encodeURIComponent(userId)}&blobName=${encodeURIComponent(blobName)}`;
    }, [url, userId]);

    const imgRef = useRef<HTMLImageElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = imgRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { rootMargin: '200px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <img
            ref={imgRef}
            src={visible ? src : undefined}
            alt="receipt"
            className={className}
            style={{ cursor: onClick ? 'zoom-in' : undefined, minHeight: visible ? undefined : 120, ...style }}
            onClick={() => onClick?.(src)}
        />
    );
}

export default function Conversation() {
    const { appData } = useAppContext();
    const userId = appData?.user?.userId || '';
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { conv: IConversation; backTo?: string } | null;
    const selectedConv = state?.conv ?? null;
    const backTo = state?.backTo ?? '/Chat';
    const _api = useMemo(() => new SConsciousApiService(), []);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const convMenuRef = useRef<HTMLDivElement>(null);
    const msgMenuRef = useRef<HTMLDivElement>(null);

    const [messages, setMessages] = useState<IMessage[]>([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [newMsg, setNewMsg] = useState('');
    const [sending, setSending] = useState(false);
    const [attachment, setAttachment] = useState<IAttachmentPreview | null>(null);
    const [uploading, setUploading] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);
    const [showConvMenu, setShowConvMenu] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [friendReqStatus, setFriendReqStatus] = useState<'idle' | 'sending' | 'sent' | 'exists'>('idle');
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [msgMenu, setMsgMenu] = useState<string | null>(null);
    const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');

    // Redirect if no conversation in state
    useEffect(() => {
        if (!selectedConv) navigate('/Chat', { replace: true });
    }, [selectedConv, navigate]);

    // Load messages on mount
    useEffect(() => {
        if (!selectedConv || selectedConv.pending) return;
        setLoadingMsgs(true);
        _api.getChatMessages(selectedConv.conversationId, userId).then(msgs => {
            setMessages(msgs);
            setLoadingMsgs(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-scroll to bottom when messages change — instant on first load, smooth after
    const hasScrolledRef = useRef(false);
    useEffect(() => {
        if (!messages.length) return;
        const behavior = hasScrolledRef.current ? 'smooth' : 'auto';
        messagesEndRef.current?.scrollIntoView({ behavior });
        hasScrolledRef.current = true;
    }, [messages]);

    // Auto-refresh messages every 12s
    useEffect(() => {
        if (!selectedConv || selectedConv.pending) return;
        const interval = setInterval(async () => {
            const msgs = await _api.getChatMessages(selectedConv.conversationId, userId);
            setMessages(msgs);
        }, 12000);
        return () => clearInterval(interval);
    }, [selectedConv, _api, userId]);

    // Close menus on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (convMenuRef.current && !convMenuRef.current.contains(e.target as Node)) setShowConvMenu(false);
            if (msgMenuRef.current && !msgMenuRef.current.contains(e.target as Node)) setMsgMenu(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close emoji picker on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowEmoji(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    // ── attachment helpers ─────────────────────────────────────────────
    const loadAttachment = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            const displayUrl = URL.createObjectURL(file);
            setAttachment({
                dataUrl,
                displayUrl,
                fileName: file.name,
                mimeType: file.type || 'application/octet-stream',
                isImage: file.type.startsWith('image/'),
            });
        };
        reader.readAsDataURL(file);
    }, []);

    const clearAttachment = useCallback(() => {
        if (attachment?.displayUrl) URL.revokeObjectURL(attachment.displayUrl);
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [attachment]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) loadAttachment(file);
    };

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = Array.from(e.clipboardData.items);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        if (imageItem) {
            e.preventDefault();
            const file = imageItem.getAsFile();
            if (file) loadAttachment(file);
        }
    }, [loadAttachment]);

    // ── cancel pending invite ──────────────────────────────────────────
    const cancelInvite = async () => {
        if (!selectedConv) return;
        const inviteId = selectedConv.inviteId || selectedConv.conversationId.replace(/^invite_/, '');
        await _api.cancelInvite(userId, inviteId);
        navigate(backTo, { replace: true });
    };

    // ── delete conversation ────────────────────────────────────────────
    const deleteConversation = async () => {
        if (!selectedConv) return;
        const ok = await _api.deleteChatConversation(selectedConv.conversationId, userId);
        if (ok) navigate(backTo, { replace: true });
        setConfirmDelete(false);
        setShowConvMenu(false);
    };

    // ── delete message ─────────────────────────────────────────────────
    const deleteMessage = async (messageId: string) => {
        setMsgMenu(null);
        const ok = await _api.deleteChatMessage(messageId, userId);
        if (ok) setMessages(prev => prev.filter(m => m.messageId !== messageId));
    };

    // ── edit message ───────────────────────────────────────────────────
    const startEdit = (msg: IMessage) => {
        setMsgMenu(null);
        setEditingMsgId(msg.messageId);
        setEditText(msg.message);
    };

    const submitEdit = async () => {
        if (!editingMsgId || !editText.trim()) return;
        const ok = await _api.editChatMessage(editingMsgId, userId, editText.trim());
        if (ok) setMessages(prev => prev.map(m => m.messageId === editingMsgId ? { ...m, message: editText.trim() } : m));
        setEditingMsgId(null);
        setEditText('');
    };

    const cancelEdit = () => { setEditingMsgId(null); setEditText(''); };

    // ── send message ───────────────────────────────────────────────────
    const sendMessage = async () => {
        if ((!newMsg.trim() && !attachment) || !selectedConv || sending) return;
        setSending(true);
        setShowEmoji(false);

        const text = newMsg.trim();
        setNewMsg('');

        let attachmentUrl: string | undefined;
        let attachmentName: string | undefined;

        if (attachment) {
            setUploading(true);
            const result = await _api.uploadChatAttachment(userId, attachment.dataUrl, attachment.fileName);
            setUploading(false);
            if (result) {
                attachmentUrl = result.url;
                attachmentName = attachment.fileName;
            }
            clearAttachment();
        }

        const ok = await _api.sendChatMessage(userId, selectedConv.otherUserId, text, selectedConv.type, attachmentUrl, attachmentName);
        if (ok) {
            setMessages(prev => [...prev, {
                messageId: `local_${Date.now()}`,
                conversationId: selectedConv.conversationId,
                fromUserId: userId,
                fromName: appData?.user?.userName || 'You',
                message: text,
                type: selectedConv.type,
                createdAt: new Date().toISOString(),
                attachmentUrl,
                attachmentName,
            }]);
            const msgs = await _api.getChatMessages(selectedConv.conversationId, userId);
            setMessages(msgs);
        }
        setSending(false);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
        if (e.key === 'Escape') setShowEmoji(false);
    };

    if (!selectedConv) return null;

    const isFeedback = selectedConv.type === 'feedback';
    const isSelf = selectedConv.otherUserId === userId;
    const headerBg = isFeedback ? 'rgba(0,80,200,0.08)' : 'rgba(0,0,0,0.08)';
    const canSend = !sending && !uploading && (!!newMsg.trim() || !!attachment);
    const goBack = () => navigate(backTo);

    // ── Pending invite view ────────────────────────────────────────────
    if (selectedConv.pending) {
        return (
            <Stack style={{ width: '100%', height: '100%', maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div className="chat-header">
                    <button onClick={goBack} style={{ background: '#000', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', fontSize: 16, flexShrink: 0, color: '#ffcc00', marginRight: 4 }}>←</button>
                    <FontIcon iconName="Mail" style={{ color: '#ffcc00', fontSize: 22 }} />
                    <div style={{ flex: 1 }}>
                        <div className="chat-header__name">{selectedConv.otherUserName}</div>
                        <div className="chat-header__sub">Invited</div>
                    </div>
                    <div style={{ position: 'relative' }} ref={convMenuRef}>
                        <button className="chat-icon-btn" style={{ fontSize: '1.3rem', opacity: 0.6 }} onClick={() => setShowConvMenu(v => !v)} title="Options">⋮</button>
                        {showConvMenu && (
                            <div className="chat-dropdown">
                                <button className="chat-dropdown__item chat-dropdown__item--danger" onClick={() => { setShowConvMenu(false); setConfirmDelete(true); }}>✕ Cancel invite</button>
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', gap: 12 }}>
                    <div><FontIcon iconName="Mail" style={{ color: '#ffcc00', fontSize: 48 }} /></div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{selectedConv.otherUserName}</div>
                    <div style={{ opacity: 0.55, fontSize: '0.88rem', lineHeight: 1.5 }}>
                        Waiting for them to join sConscious.<br />They'll appear here once they sign up.
                    </div>
                </div>
                {confirmDelete && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                        <div style={{ background: '#ffcc00', borderRadius: 16, padding: '24px 20px', maxWidth: 300, width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 8 }}>Cancel this invite?</div>
                            <div style={{ fontSize: '0.85em', opacity: 0.65, marginBottom: 20 }}>The invite to {selectedConv.otherUserName} will be cancelled.</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="actionButton" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setConfirmDelete(false)}>Back</button>
                                <button className="actionButton" style={{ flex: 1, justifyContent: 'center', background: '#c0392b', color: '#fff', border: 'none' }} onClick={cancelInvite}>Cancel invite</button>
                            </div>
                        </div>
                    </div>
                )}
            </Stack>
        );
    }

    return (
        <Stack style={{ width: '100%', height: '100%', maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>

            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.doc,.docx,.txt,.zip,.csv,.xlsx" style={{ display: 'none' }} onChange={handleFileChange} />

            {/* Header */}
            <div className="chat-header" style={{ background: headerBg }}>
                    <button onClick={goBack} style={{ background: '#000', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', fontSize: 16, flexShrink: 0, color: '#ffcc00', marginRight: 4 }}>←</button>
                <span style={{ fontSize: 17 }}>{isFeedback ? '🛡' : isSelf ? '🪞' : '👤'}</span>
                <div style={{ flex: 1 }}>
                    <div
                        className="chat-header__name"
                        onClick={!isFeedback && !isSelf ? () => navigate(`/Profile?userId=${encodeURIComponent(selectedConv.otherUserId)}`) : undefined}
                        style={!isFeedback && !isSelf ? { cursor: 'pointer' } : undefined}
                    >{selectedConv.otherUserName}</div>
                    <div className="chat-header__sub">{isFeedback ? 'Support' : isSelf ? 'Me' : 'Friend'}</div>
                </div>
                {/* 3-dot conv menu */}
                <div style={{ position: 'relative' }} ref={convMenuRef}>
                    <button className="chat-icon-btn" style={{ fontSize: '1.3rem', opacity: 0.6 }} onClick={() => setShowConvMenu(v => !v)} title="Options">⋮</button>
                    {showConvMenu && (
                        <div className="chat-dropdown">
                            {!isFeedback && !isSelf && (
                                <button
                                    className="chat-dropdown__item"
                                    onClick={() => { setShowConvMenu(false); navigate(`/Profile?userId=${encodeURIComponent(selectedConv.otherUserId)}`); }}
                                >👁 View profile</button>
                            )}
                            {!isFeedback && (
                                <button
                                    className="chat-dropdown__item"
                                    disabled={friendReqStatus === 'sending' || friendReqStatus === 'sent' || friendReqStatus === 'exists'}
                                    onClick={async () => {
                                        setFriendReqStatus('sending');
                                        setShowConvMenu(false);
                                        const res = await _api.sendFriendRequest(userId, selectedConv.otherUserId);
                                        setFriendReqStatus(res.alreadyExists ? 'exists' : res.success ? 'sent' : 'idle');
                                        setTimeout(() => setFriendReqStatus('idle'), 4000);
                                    }}
                                >
                                    👤 {friendReqStatus === 'sending' ? 'Sending…' : friendReqStatus === 'sent' ? 'Request sent!' : friendReqStatus === 'exists' ? 'Already friends' : 'Send friend request'}
                                </button>
                            )}
                            <button className="chat-dropdown__item chat-dropdown__item--danger" onClick={() => { setShowConvMenu(false); setConfirmDelete(true); }}>🗑 Delete chat</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Lightbox overlay */}
            {lightboxUrl && (
                <div onClick={() => setLightboxUrl(null)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}>
                    <img src={lightboxUrl} alt="attachment" style={{ maxWidth: '100%', maxHeight: '92%', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', cursor: 'default' }} onClick={e => e.stopPropagation()} />
                    <button className="closeBtn" style={{ position: 'absolute', top: 16, right: 16, zIndex: 301 }} onClick={() => setLightboxUrl(null)}>✕</button>
                </div>
            )}

            {/* Confirm delete overlay */}
            {confirmDelete && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                    <div style={{ background: '#ffcc00', borderRadius: 16, padding: '24px 20px', maxWidth: 300, width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                        <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 8 }}>Delete this chat?</div>
                        <div style={{ fontSize: '0.85em', opacity: 0.65, marginBottom: 20 }}>All messages will be permanently deleted for both parties.</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="actionButton" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setConfirmDelete(false)}>Cancel</button>
                            <button className="actionButton" style={{ flex: 1, justifyContent: 'center', background: '#c0392b', color: '#fff', border: 'none' }} onClick={deleteConversation}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages */}
            <div className="chat-messages">
                {loadingMsgs && <div style={{ opacity: 0.5, fontSize: '0.85em', textAlign: 'center' }}>Loading…</div>}
                {!loadingMsgs && messages.length === 0 && (
                    <div style={{ opacity: 0.4, fontSize: '0.85em', textAlign: 'center', marginTop: 32 }}>No messages yet. Say hello! 👋</div>
                )}
                {messages.map(msg => {
                    const isOwn = msg.fromUserId === userId;
                    const senderLabel = isOwn ? 'Me' : (msg.fromName || selectedConv.otherUserName);
                    const isEditing = editingMsgId === msg.messageId;
                    const menuOpen = msgMenu === msg.messageId;
                    const isReceiptSuccess = !!msg.attachmentName?.startsWith('receipt:');
                    const isReceiptFailed = !!msg.attachmentName?.startsWith('receipt-failed:');
                    const receiptId = isReceiptSuccess ? msg.attachmentName!.slice(8) : null;
                    const failedBlobName = isReceiptFailed ? decodeURIComponent(msg.attachmentName!.slice(15)) : null;

                    return (
                        <div key={msg.messageId} className={`chat-msg-wrap${isOwn ? ' chat-msg-wrap--own' : ''}`}>
                            {/* Menu button left of own bubble */}
                            {isOwn && (
                                <div style={{ position: 'relative', flexShrink: 0 }} ref={menuOpen ? msgMenuRef : undefined}>
                                    <button className="chat-menu-btn" onClick={() => setMsgMenu(menuOpen ? null : msg.messageId)}>⋮</button>
                                    {menuOpen && (
                                        <div className="chat-dropdown chat-dropdown--left">
                                            <button className="chat-dropdown__item" onClick={() => startEdit(msg)}>✏️ Edit</button>
                                            <button className="chat-dropdown__item chat-dropdown__item--danger" onClick={() => deleteMessage(msg.messageId)}>🗑 Delete</button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={`chat-bubble${isOwn ? ' chat-bubble--own' : ''}`}>
                                <div className="chat-bubble__sender">{senderLabel}</div>
                                {isEditing ? (
                                    <div>
                                        <textarea
                                            className="chat-edit-textarea"
                                            value={editText}
                                            onChange={e => setEditText(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); } if (e.key === 'Escape') cancelEdit(); }}
                                            autoFocus
                                            rows={2}
                                        />
                                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                            <button className="actionButton" style={{ fontSize: '0.75rem', padding: '2px 10px', background: '#111', color: '#ffcc00', border: 'none' }} onClick={submitEdit}>Save</button>
                                            <button className="actionButton" style={{ fontSize: '0.75rem', padding: '2px 10px' }} onClick={cancelEdit}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {msg.message && <div className="chat-bubble__text">{msg.message}</div>}
                                        {msg.attachmentUrl && isImageUrl(msg.attachmentUrl) && (
                                            <div>
                                                <ChatImage
                                                    url={msg.attachmentUrl}
                                                    userId={userId}
                                                    className="chat-bubble__img"
                                                    style={{ marginTop: msg.message ? 5 : 0 }}
                                                    onClick={(dataUrl) => {
                                                        if (isReceiptSuccess && receiptId) {
                                                            navigate(`/receipt?receiptId=${receiptId}`);
                                                        } else if (isReceiptFailed && failedBlobName) {
                                                            navigate(`/receipt?blobName=${encodeURIComponent(failedBlobName)}&scanError=${encodeURIComponent(msg.message || 'Scan failed')}`);
                                                        } else {
                                                            setLightboxUrl(dataUrl);
                                                        }
                                                    }}
                                                />
                                                {(isReceiptSuccess || isReceiptFailed) && (
                                                    <div style={{ fontSize: '0.72em', opacity: 0.6, marginTop: 2, textAlign: 'right' }}>Tap to view receipt →</div>
                                                )}
                                            </div>
                                        )}
                                        {msg.attachmentUrl && isPdfUrl(msg.attachmentUrl) && (
                                            <embed src={msg.attachmentUrl} type="application/pdf" style={{ width: '100%', height: 300, borderRadius: 8, marginTop: msg.message ? 5 : 0, display: 'block' }} />
                                        )}
                                        {msg.attachmentUrl && !isImageUrl(msg.attachmentUrl) && !isPdfUrl(msg.attachmentUrl) && (
                                            <div className="chat-bubble__file" style={{ marginTop: msg.message ? 5 : 0 }}>
                                                <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>📎</span>
                                                <span>{msg.attachmentName || 'File'}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="chat-bubble__time">{formatTime(msg.createdAt)}</div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Emoji picker */}
            {showEmoji && (
                <div className="chat-emoji-picker">
                    {EMOJIS.map(em => (
                        <button key={em} onClick={() => { setNewMsg(prev => prev + em); textareaRef.current?.focus(); }}>{em}</button>
                    ))}
                </div>
            )}

            {/* Attachment preview */}
            {attachment && (
                <div className="chat-attachment-preview">
                    {attachment.isImage
                        ? <img src={attachment.displayUrl} alt="preview" style={{ height: 46, width: 46, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                        : <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📎</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="chat-attachment-preview__name">{attachment.fileName}</div>
                        {uploading && <div className="chat-attachment-preview__uploading">Uploading…</div>}
                    </div>
                    <button className="chat-icon-btn" style={{ opacity: 0.5 }} onClick={clearAttachment}>✕</button>
                </div>
            )}

            {/* Input bar */}
            <div className="chat-input-bar">
                <button className={`chat-icon-btn${showEmoji ? ' chat-icon-btn--active' : ''}`} onClick={() => setShowEmoji(v => !v)} title="Emoji">😊</button>
                <button className="chat-icon-btn" onClick={() => fileInputRef.current?.click()} title="Attach file">📎</button>
                <textarea
                    ref={textareaRef}
                    className="chat-textarea"
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={onKeyDown}
                    onPaste={handlePaste}
                    rows={1}
                    placeholder="Type a message…"
                />
                <button className="chat-send-btn" onClick={sendMessage} disabled={!canSend}>
                    {uploading ? '⏫' : sending ? '…' : 'Send'}
                </button>
            </div>
        </Stack>
    );
}
